import { EyeSlashIcon, PencilIcon } from '@heroicons/react/20/solid';
import { memo, useCallback, useState } from 'react';

// Admin-only edit / hide controls for a Release Center message (#1513). Both
// reuse the existing admin patch endpoint (/api/v1/admin/message/update/:id,
// authMiddleware(['admin'])): edit patches `content`, hide soft-hides via
// `visible: false` (the row is kept, not hard-deleted, and can be restored from
// the admin Messages table). The feed query filters `message.visible = true`, so
// a hidden message drops out for everyone (members and admins) on next refresh;
// onHidden lets the parent grey it out immediately for feedback.
//
// Edit now happens IN PLACE (#1640): the pencil flips the parent <Message> into
// an inline, full-width editor that replaces the message text (like Slack),
// instead of opening a centered modal (#1609). The parent owns the edit state
// and the save request, so this menu just triggers it via `onEdit`.
const MessageEditMenu = ({
  id,
  updateRequest,
  onEdit,
  onHidden,
}: {
  id: API.Id;
  content: string;
  updateRequest: any;
  onEdit?: () => void;
  onHidden?: () => void;
}) => {
  const [loading, setLoading] = useState(false);

  const hide = useCallback(async () => {
    if (loading) {
      return;
    }

    if (
      typeof window !== 'undefined' &&
      !window.confirm(
        'Hide this message from all members? It stays in the admin Messages table and can be restored there.',
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      await updateRequest.run(
        { data: { visible: false } },
        {
          req: { input: `/api/v1/admin/message/update/${id}` },
          dontStoreError: true,
          dontAlterState: true,
        },
      );
      onHidden?.();
    } catch {
      // errors are swallowed (dontStoreError); nothing more to do here
    } finally {
      setLoading(false);
    }
  }, [loading, updateRequest, id, onHidden]);

  return (
    <div className="flex flex-row items-center divide-x dark:divide-zinc-700">
      <button
        type="button"
        disabled={loading}
        onClick={onEdit}
        aria-label="Edit message text"
        title="Edit message text"
        className="p-2 text-zinc-600 hover:bg-emerald-500/5 disabled:opacity-50 dark:text-zinc-400"
      >
        <PencilIcon className="h-4 w-auto shrink-0" aria-hidden="true" />
      </button>

      <button
        type="button"
        disabled={loading}
        onClick={hide}
        title="Hide message from members"
        className="relative p-2 hover:bg-red-500/5 disabled:opacity-50"
      >
        <EyeSlashIcon
          className="h-4 w-auto shrink-0 text-zinc-600 hover:text-red-500 dark:text-zinc-400"
          aria-hidden="true"
        />
      </button>
    </div>
  );
};

export default memo(
  MessageEditMenu,
  (prevProps, nextProps) =>
    prevProps.id === nextProps.id &&
    prevProps.content === nextProps.content &&
    prevProps.updateRequest === nextProps.updateRequest &&
    prevProps.onEdit === nextProps.onEdit &&
    prevProps.onHidden === nextProps.onHidden,
);
